import React, { useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Share2, Timer, Gauge, Flame, MountainSnow, Activity, Footprints, ChevronRight, ChevronLeft } from "lucide-react";
import RunMap from "./RunMap";
import { computeSplits, elevationSeries, fmtPace, fmtTime, fmtDistance } from "../lib/runAnalytics";
import { toast } from "sonner";

const METERS_PER_MILE = 1609.344;

/** RunSummary
 *  Strava-style post-run summary: hero map, headline stats, per-km/mile splits, elevation chart.
 *  `run` = full run doc from /api/runs/{id} or /api/runs.
 */
export default function RunSummary({ run, onClose, isModal = false }) {
    const [unit, setUnit] = useState("km");
    const [exporting, setExporting] = useState(false);
    const cardRef = useRef(null);

    const splits = useMemo(() => computeSplits(run.gps_path || [], unit), [run.gps_path, unit]);
    const elev = useMemo(() => elevationSeries(run.gps_path || []), [run.gps_path]);

    const elevGain = useMemo(() => {
        if (elev.length < 2) return 0;
        let gain = 0;
        for (let i = 1; i < elev.length; i++) {
            const d = elev[i].alt - elev[i - 1].alt;
            if (d > 0) gain += d;
        }
        return Math.round(gain);
    }, [elev]);

    const totalUnits = unit === "mi" ? run.distance_meters / METERS_PER_MILE : run.distance_meters / 1000;
    const avgPace = totalUnits > 0 ? run.duration_seconds / 60 / totalUnits : 0;

    const maxPaceShown = useMemo(() => {
        const paces = splits.filter((s) => !s.partial).map((s) => s.pace_min_per_unit);
        if (paces.length === 0) return avgPace || 6;
        return Math.max(...paces);
    }, [splits, avgPace]);

    const exportPng = async () => {
        if (!cardRef.current) return;
        setExporting(true);
        try {
            const canvas = await html2canvas(cardRef.current, {
                backgroundColor: "#0d0d0d",
                useCORS: true,
                scale: 2,
                logging: false,
            });
            const url = canvas.toDataURL("image/png", 1.0);
            const a = document.createElement("a");
            a.href = url;
            a.download = `noexcuse-run-${run.id?.slice(0, 8) || "card"}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success("Shareable card downloaded");
        } catch (e) {
            toast.error("Couldn't render image: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    const Icon = run.activity === "walk" ? Footprints : Activity;
    const date = run.started_at ? new Date(run.started_at) : null;

    const body = (
        <div ref={cardRef} className="bg-charcoal-900" data-testid="run-summary">
            {/* Hero Map */}
            <div className="relative">
                <RunMap gpsPath={run.gps_path || []} mode="summary" height={260} />
                <div className="absolute top-3 left-3 surface-2 px-2 py-1 flex items-center gap-1.5">
                    <Icon size={12} className="text-neon-green" />
                    <span className="label-cap text-[10px] text-zinc-300">{run.activity}</span>
                </div>
                {date && (
                    <div className="absolute top-3 right-3 surface-2 px-2 py-1">
                        <span className="label-cap text-[10px] text-zinc-300">
                            {date.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ·{" "}
                            {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                    </div>
                )}
            </div>

            {/* Headline stats */}
            <div className="px-5 pt-5 pb-3" data-testid="summary-headline">
                <div className="flex items-end gap-2">
                    <span className="font-display font-black text-6xl text-neon-green leading-none" data-testid="summary-distance">
                        {fmtDistance(run.distance_meters, unit)}
                    </span>
                    <span className="label-cap text-zinc-400 text-xs mb-2">{unit}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4">
                    <Stat icon={Timer} label="Time" value={fmtTime(run.duration_seconds)} testid="summary-time" />
                    <Stat icon={Gauge} label={`Pace / ${unit}`} value={fmtPace(avgPace)} testid="summary-pace" />
                    <Stat icon={Flame} label="kcal" value={Math.round(run.calories_burned || 0)} testid="summary-kcal" />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                    <Stat label="Avg Speed" value={`${(run.avg_speed_kmh || 0).toFixed(1)}`} unit="km/h" testid="summary-avg-speed" />
                    <Stat label="Max Speed" value={`${(run.max_speed_kmh || 0).toFixed(1)}`} unit="km/h" testid="summary-max-speed" />
                    <Stat icon={MountainSnow} label="Elev Gain" value={elev.length > 1 ? `+${elevGain}` : "—"} unit="m" testid="summary-elev" />
                </div>
            </div>

            {/* Unit toggle */}
            <div className="px-5 mt-2">
                <div className="inline-flex border border-charcoal-500" data-testid="unit-toggle">
                    {["km", "mi"].map((u) => (
                        <button
                            key={u}
                            onClick={() => setUnit(u)}
                            data-testid={`unit-${u}`}
                            className={`px-3 py-1 label-cap text-[10px] ${
                                unit === u
                                    ? "bg-neon-green text-charcoal-900"
                                    : "bg-transparent text-zinc-400"
                            }`}
                        >
                            {u === "km" ? "Kilometers" : "Miles"}
                        </button>
                    ))}
                </div>
            </div>

            {/* Splits */}
            {splits.length > 0 && (
                <div className="px-5 mt-5" data-testid="summary-splits">
                    <h3 className="font-display font-black uppercase tracking-widest text-xs text-neon-orange mb-2">
                        Splits ({unit})
                    </h3>
                    <div className="space-y-1.5">
                        {splits.map((s) => {
                            const widthPct = Math.min(100, (s.pace_min_per_unit / maxPaceShown) * 100);
                            return (
                                <div key={s.idx} className="flex items-center gap-2 text-xs" data-testid={`split-${s.idx}`}>
                                    <span className="w-6 label-cap text-zinc-500">{s.label}</span>
                                    <div className="flex-1 h-5 surface-1 relative overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-neon-green/40 to-neon-green/10"
                                            style={{ width: `${widthPct}%` }}
                                        />
                                        <span className="absolute inset-0 flex items-center px-2 font-mono text-[11px] text-white">
                                            {fmtPace(s.pace_min_per_unit)} / {unit}
                                            {s.partial && <span className="ml-2 text-zinc-500">·partial</span>}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Elevation chart */}
            {elev.length > 2 && (
                <div className="px-5 mt-5" data-testid="summary-elevation">
                    <h3 className="font-display font-black uppercase tracking-widest text-xs text-neon-orange mb-2">
                        Elevation
                    </h3>
                    <ElevationChart points={elev} />
                </div>
            )}

            {/* Branding stamp — visible in PNG export */}
            <div className="px-5 mt-5 pb-5 flex items-center justify-between">
                <div className="label-cap text-[10px] text-zinc-600">noexcuse · v1.1</div>
                <div className="font-display font-black uppercase tracking-widest text-xs text-neon-green">
                    No Excuse
                </div>
            </div>
        </div>
    );

    if (isModal) {
        return (
            <div
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto"
                onClick={onClose}
            >
                <div
                    className="bg-charcoal-900 w-full max-w-[480px] my-4 border border-charcoal-500"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="px-5 pt-4 flex items-center justify-between">
                        <button
                            onClick={onClose}
                            data-testid="summary-close"
                            className="text-zinc-400 text-xs label-cap flex items-center gap-1"
                        >
                            <ChevronLeft size={14} /> Done
                        </button>
                        <button
                            onClick={exportPng}
                            disabled={exporting}
                            data-testid="summary-share"
                            className="text-neon-orange text-xs label-cap flex items-center gap-1 disabled:opacity-50"
                        >
                            <Share2 size={12} /> {exporting ? "Rendering..." : "Share"}
                        </button>
                    </div>
                    {body}
                </div>
            </div>
        );
    }

    return (
        <div data-testid="run-summary-page">
            <div className="px-5 pt-4 flex items-center justify-between">
                {onClose && (
                    <button onClick={onClose} className="text-zinc-400 text-xs label-cap flex items-center gap-1">
                        <ChevronLeft size={14} /> Back
                    </button>
                )}
                <button
                    onClick={exportPng}
                    disabled={exporting}
                    data-testid="summary-share"
                    className="ml-auto text-neon-orange text-xs label-cap flex items-center gap-1 disabled:opacity-50"
                >
                    <Share2 size={12} /> {exporting ? "Rendering..." : "Share PNG"}
                </button>
            </div>
            {body}
        </div>
    );
}

function Stat({ icon: Icon, label, value, unit, testid }) {
    return (
        <div className="surface-1 p-3" data-testid={testid}>
            <div className="label-cap text-[10px] text-zinc-500 flex items-center gap-1">
                {Icon && <Icon size={10} />} {label}
            </div>
            <div className="font-display font-black text-xl mt-1 leading-none">
                {value}
                {unit && <span className="text-[10px] text-zinc-400 ml-1">{unit}</span>}
            </div>
        </div>
    );
}

function ElevationChart({ points }) {
    if (points.length < 2) return null;
    const W = 320, H = 80;
    const xs = points.map((p) => p.d_m);
    const ys = points.map((p) => p.alt);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const xr = maxX - minX || 1;
    const yr = maxY - minY || 1;
    const path = points
        .map((p, i) => {
            const x = ((p.d_m - minX) / xr) * W;
            const y = H - ((p.alt - minY) / yr) * H;
            return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join(" ");
    const area = `${path} L ${W} ${H} L 0 ${H} Z`;
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20 surface-1" preserveAspectRatio="none">
            <defs>
                <linearGradient id="elevGrad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#39FF14" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="#39FF14" stopOpacity="0.05" />
                </linearGradient>
            </defs>
            <path d={area} fill="url(#elevGrad)" />
            <path d={path} fill="none" stroke="#39FF14" strokeWidth="1.5" />
            <text x="4" y="11" fontSize="9" fill="#666" fontFamily="monospace">{Math.round(maxY)}m</text>
            <text x="4" y={H - 3} fontSize="9" fill="#666" fontFamily="monospace">{Math.round(minY)}m</text>
        </svg>
    );
}
